import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CampaignFilterService } from '../services/campaignFilterService';
import { Campaign } from '../types';
import moment from 'moment-timezone';
import { exportWeeklyAttendanceToCSV, exportWeeklyAttendanceToExcel, exportWeeklyAttendanceToPDF } from '../utils/exportUtils';
import ShareReportDialog from '../components/ShareReportDialog';
import SearchableSelect from '../components/SearchableSelect';

interface WeeklyAttendanceData {
  employeeId: string;
  name: string;
  weeks: {
    [weekKey: string]: {
      weekStart: string;
      weekEnd: string;
      weeklyTotal: string;
      weeklyTotalMinutes: number;
      days: {
        [dateKey: string]: {
          date: string;
          dayOfWeek: string;
          dailyTotal: string;
          dailyTotalMinutes: number;
          entries: Array<{
            clockIn: string;
            clockOut: string | null;
            totalHours: string;
            campaign: string;
          }>;
          isCoverage?: boolean;
          actualWorker?: {
            id: string;
            displayName: string;
            employeeId: string;
          };
          campaign?: string;
        };
      };
    };
  };
}

interface AttendanceReportResponse {
  success: boolean;
  data: {
    reportData: { [userId: string]: WeeklyAttendanceData };
    dateRange: {
      startDate: string;
      endDate: string;
    };
    totalEmployees: number;
    totalWeeks: number;
  };
}

const WeeklyAttendanceReportPage: React.FC = () => {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState(moment().subtract(2, 'weeks').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [exportFormat, setExportFormat] = useState<'json' | 'csv' | 'excel' | 'pdf'>('json');
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Check if user can share reports (supervisor and above)
  const canShareReports = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Fetch employees for the dropdown
  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiClient.users.getAll(),
    enabled: !!user && ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Fetch campaigns for filter dropdown
  const { data: campaignsData } = useQuery<{ data: { success: boolean; data: Campaign[] } }, Error>({
    queryKey: ['campaigns', startDate, endDate],
    queryFn: () => apiClient.campaigns.getAll({
      includeWithActivityFrom: startDate,
      includeWithActivityTo: endDate,
    }),
    enabled: !!user && ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || ''),
  });

  // Filter campaigns based on user's assigned campaigns
  const availableCampaigns = campaignsData?.data?.data ? 
    CampaignFilterService.getUserAccessibleCampaigns(user!, campaignsData.data.data) : [];

  // Fetch attendance report (always JSON - exports are done on frontend)
  const { data: reportData, isLoading, error, refetch } = useQuery<AttendanceReportResponse>({
    queryKey: ['attendance-report', startDate, endDate, selectedEmployee, selectedCampaign],
    queryFn: () => apiClient.attendanceReport.getReport({
      startDate,
      endDate,
      employeeId: selectedEmployee,
      campaignId: selectedCampaign,
      format: 'json' // Always fetch JSON - exports are done on frontend
    }).then(res => res.data),
    enabled: !!user && !!startDate && !!endDate,
  });

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    if (!reportData?.data?.reportData) {
      alert('No data available to export. Please load the report first.');
      return;
    }

    const { reportData: reportDataObj, dateRange } = reportData.data;

    // Use frontend-based exports like other reports
    if (format === 'csv') {
      exportWeeklyAttendanceToCSV(reportDataObj, dateRange);
    } else if (format === 'excel') {
      exportWeeklyAttendanceToExcel(reportDataObj, dateRange);
    } else if (format === 'pdf') {
      exportWeeklyAttendanceToPDF(reportDataObj, dateRange);
    }
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCSV = () => {
    handleExport('csv');
    handleExportMenuClose();
  };

  const handleExportExcel = () => {
    handleExport('excel');
    handleExportMenuClose();
  };

  const handleExportPDF = () => {
    handleExport('pdf');
    handleExportMenuClose();
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    return moment(time, 'HH:mm').format('h:mm A');
  };

  const getWeekNumber = (weekStart: string) => {
    return moment(weekStart).week();
  };

  const renderWeeklyTimesheet = (employee: WeeklyAttendanceData, weekKey: string, weekData: any) => {
    const weekNumber = getWeekNumber(weekData.weekStart);
    const weekStartFormatted = moment(weekData.weekStart).format('M/D/YYYY');
    const weekEndFormatted = moment(weekData.weekEnd).format('M/D/YYYY');

    return (
      <Card key={weekKey} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
        <CardContent>
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
              WEEKLY TIMESHEET
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Employee Name:</strong> {employee.name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Period:</strong> From {weekStartFormatted} to {weekEndFormatted}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ border: '1px solid #ccc' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>Week Day</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>IN</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>Break Start</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>Break End</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>Lunch Start</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>Lunch End</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>OUT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>Total Hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((dayName) => {
                  const dayData = Object.values(weekData.days).find((day: any) => day.dayOfWeek === dayName) as any;
                  
                  // Debug logging
                  if (dayData) {
                    console.log(`📊 Day ${dayName} data:`, {
                      date: dayData.date,
                      dayOfWeek: dayData.dayOfWeek,
                      clockIn: dayData.clockIn,
                      breakStart: dayData.breakStart,
                      breakEnd: dayData.breakEnd,
                      lunchStart: dayData.lunchStart,
                      lunchEnd: dayData.lunchEnd,
                      clockOut: dayData.clockOut,
                      entries: dayData.entries
                    });
                  }
                  
                  if (!dayData) {
                    return (
                      <TableRow key={dayName}>
                        <TableCell sx={{ border: '1px solid #ccc' }}>{dayName}</TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>-</TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>-</TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>-</TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>-</TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>-</TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>-</TableCell>
                        <TableCell sx={{ border: '1px solid #ccc' }}>0:00</TableCell>
                      </TableRow>
                    );
                  }

                  // Use raw times directly from backend (more reliable than inferring from entry pairs)
                  // Backend now provides clockIn, breakStart, breakEnd, lunchStart, lunchEnd, clockOut directly
                  // Handle null, undefined, or empty string values
                  const clockIn = (dayData.clockIn && dayData.clockIn !== 'null' && dayData.clockIn !== null) ? dayData.clockIn : '-';
                  const breakStart = (dayData.breakStart && dayData.breakStart !== 'null' && dayData.breakStart !== null) ? dayData.breakStart : '-';
                  const breakEnd = (dayData.breakEnd && dayData.breakEnd !== 'null' && dayData.breakEnd !== null) ? dayData.breakEnd : '-';
                  const lunchStart = (dayData.lunchStart && dayData.lunchStart !== 'null' && dayData.lunchStart !== null) ? dayData.lunchStart : '-';
                  const lunchEnd = (dayData.lunchEnd && dayData.lunchEnd !== 'null' && dayData.lunchEnd !== null) ? dayData.lunchEnd : '-';
                  const clockOut = (dayData.clockOut && dayData.clockOut !== 'null' && dayData.clockOut !== null) ? dayData.clockOut : '-';

                  return (
                    <TableRow 
                      key={dayName}
                      sx={dayData.isCoverage ? { 
                        backgroundColor: '#e3f2fd', // Light blue highlight for coverage
                        '&:hover': {
                          backgroundColor: '#bbdefb'
                        }
                      } : {}}
                    >
                      <TableCell sx={{ border: '1px solid #ccc' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {dayName}
                          {dayData.isCoverage && dayData.actualWorker && (
                            <Tooltip 
                              title={
                                <Box>
                                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Coverage Entry
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Covered by: {dayData.actualWorker.displayName} (ID: {dayData.actualWorker.employeeId})
                                  </Typography>
                                  {dayData.campaign && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      Campaign: {dayData.campaign}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                    This timesheet shows the scheduled employee for invoicing. Actual worker shown in note.
                                  </Typography>
                                </Box>
                              }
                              arrow
                              placement="right"
                            >
                              <PersonAddIcon 
                                color="primary" 
                                fontSize="small" 
                                sx={{ cursor: 'help' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #ccc' }}>{clockIn}</TableCell>
                      <TableCell sx={{ border: '1px solid #ccc' }}>{breakStart}</TableCell>
                      <TableCell sx={{ border: '1px solid #ccc' }}>{breakEnd}</TableCell>
                      <TableCell sx={{ border: '1px solid #ccc' }}>{lunchStart}</TableCell>
                      <TableCell sx={{ border: '1px solid #ccc' }}>{lunchEnd}</TableCell>
                      <TableCell sx={{ border: '1px solid #ccc' }}>{clockOut}</TableCell>
                      <TableCell sx={{ border: '1px solid #ccc' }}>
                        <Tooltip
                          title={
                            <Box>
                              <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Daily Total Hours:
                              </Typography>
                              <Typography variant="caption" display="block">
                                Format: HH:MM (Hours:Minutes)
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Example: {dayData.totalHours || '0:00'} = {dayData.totalHours ? dayData.totalHours.split(':')[0] : '0'} hours and {dayData.totalHours ? dayData.totalHours.split(':')[1] : '00'} minutes
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Box component="span" sx={{ cursor: 'help' }}>
                            {dayData.totalHours || '0:00'}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Weekly Total Row */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }} colSpan={7}>
                    Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #ccc' }}>
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            Weekly Total Hours:
                          </Typography>
                          <Typography variant="caption" display="block">
                            Format: HH:MM (Hours:Minutes)
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            Example: {weekData.weeklyTotal} = {weekData.weeklyTotal.split(':')[0]} hours and {weekData.weeklyTotal.split(':')[1]} minutes
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'info.light' }}>
                            Total: {Math.floor(weekData.weeklyTotalMinutes / 60)}h {weekData.weeklyTotalMinutes % 60}m
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box component="span" sx={{ cursor: 'help' }}>
                        {weekData.weeklyTotal}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Weekly Attendance Report
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate detailed weekly timesheets grouped by employee and week (2 weeks per page per employee)
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            {/* Filter Row */}
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Employee (Optional)"
                  value={selectedEmployee}
                  onChange={(value) => setSelectedEmployee(String(value))}
                  options={(employeesData?.data?.data || []).map((emp: any) => ({
                    value: emp.id,
                    label: `${emp.firstName} ${emp.lastName} (${emp.employeeId})`,
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect
                  label="Campaign (Optional)"
                  value={selectedCampaign}
                  onChange={(value) => setSelectedCampaign(String(value))}
                  options={availableCampaigns.map((campaign: Campaign) => ({
                    value: campaign.id,
                    label: campaign.name,
                  }))}
                />
              </Grid>
            </Grid>
            {/* Action Buttons Row */}
            <Grid container item xs={12} spacing={2} sx={{ mt: 1 }}>
              <Grid item>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={() => refetch()}
                    disabled={isLoading}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Generate
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleExportMenuOpen}
                    disabled={!reportData}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Export
                  </Button>
                  {canShareReports && (
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={() => setShareDialogOpen(true)}
                      disabled={!reportData}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Share
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <ExcelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export to PDF</ListItemText>
        </MenuItem>
      </Menu>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load attendance report. Please try again.
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Report Data */}
      {reportData && reportData.data && (
        <Box>
          {/* Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Report Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Date Range: {moment(reportData.data.dateRange.startDate).format('MMM D, YYYY')} - {moment(reportData.data.dateRange.endDate).format('MMM D, YYYY')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Employees: {reportData.data.totalEmployees}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Weeks: {reportData.data.totalWeeks}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Weekly Timesheets */}
          {Object.entries(reportData.data.reportData).map(([userId, employee]) => (
            <Box key={userId} sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {employee.name} ({employee.employeeId})
              </Typography>
              
              {/* Render weeks in pairs (2 weeks per page) */}
              {Object.entries(employee.weeks).map(([weekKey, weekData], index) => (
                <Box key={weekKey}>
                  {renderWeeklyTimesheet(employee, weekKey, weekData)}
                  {/* Add page break after every 2 weeks */}
                  {index % 2 === 1 && index < Object.keys(employee.weeks).length - 1 && (
                    <Box sx={{ 
                      pageBreakAfter: 'always',
                      '@media print': {
                        pageBreakAfter: 'always'
                      }
                    }} />
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}

      {/* Share Report Dialog */}
      {canShareReports && (
        <ShareReportDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          reportType="weekly-attendance"
          reportFilters={{ startDate, endDate, employeeId: selectedEmployee, campaignId: selectedCampaign }}
          reportTitle="Weekly Attendance Report"
          onShareSuccess={() => {
            console.log('Report shared successfully');
          }}
        />
      )}
    </Container>
  );
};

export default WeeklyAttendanceReportPage;
